import React, { useCallback, useEffect, useRef, useState } from "react";
import { Box, Button, Modal, ModalBody, ModalCloseButton, ModalContent, ModalOverlay, Text, useToast } from "@chakra-ui/react";

import { extractDeliveryId } from "../utils/deliveryQrLink";
import { markBusy } from "../utils/appBusy";

// jsQR은 50KB 가까이 되는데 QR 스캔은 가끔 쓰는 기능이다. 초기 번들에 넣으면
// 매번 앱을 열 때마다 그만큼을 더 받게 되므로, 스캐너를 열 때만 불러온다.
let jsQrModulePromise = null;

function loadJsQr() {
  if (!jsQrModulePromise) {
    jsQrModulePromise = import("jsqr")
      .then((mod) => mod.default)
      .catch((err) => {
        // 실패한 프로미스를 캐시에 남기면 이후 모든 시도가 즉시 실패한다.
        // 비워둬야 다시 열었을 때 청크를 새로 받아온다.
        jsQrModulePromise = null;
        throw err;
      });
  }
  return jsQrModulePromise;
}

// 스캔 프레임을 매번 원본 해상도로 훑으면 저사양 기기에서 버벅인다.
// QR은 화면 중앙에 크게 잡히므로 이 정도로 줄여도 인식에 충분하다.
const SCAN_MAX_SIDE = 480;

// 디코딩 시도 간격(ms). 화면 주사율대로 돌리면 메인 스레드가 포화된다.
const SCAN_INTERVAL_MS = 80;

function stopMediaStream(stream) {
  if (!stream) {
    return;
  }
  stream.getTracks().forEach((track) => track.stop());
}

/**
 * 영수증 QR을 앱 안에서 직접 스캔한다.
 *
 * iOS는 카메라 앱으로 QR을 찍어도 설치된 PWA가 아니라 사파리가 열리고,
 * 안드로이드도 네이버·카카오 인앱 스캐너로 찍으면 앱으로 넘어오지 않는다.
 * 앱 안에서 스캔하면 그 경로들을 모두 우회할 수 있다.
 */
export default function QrScanner({ isOpen, onClose, onDetect }) {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const rafRef = useRef(null);
  // 카메라 열기 시도를 식별한다. 기다리는 동안 사용자가 닫으면 값이 바뀌어,
  // 뒤늦게 도착한 스트림이 화면을 되살리지 않는다.
  const sessionRef = useRef(0);
  // 인식은 한 번만 처리한다. 프레임 루프가 같은 QR을 연속으로 읽기 때문이다.
  const detectedRef = useRef(false);

  const [status, setStatus] = useState("카메라를 준비하는 중입니다...");
  const toast = useToast();

  // onDetect는 목록(deliveries)에 의존해 만들어지므로 페이지가 하나씩 로딩될
  // 때마다 새 함수가 된다. 이걸 이펙트 의존성에 그대로 두면 스캔 도중 카메라가
  // 껐다 켜지기를 반복한다. 최신 콜백은 ref로 읽고 이펙트는 열림 여부만 본다.
  const onDetectRef = useRef(onDetect);

  useEffect(() => {
    onDetectRef.current = onDetect;
  }, [onDetect]);

  // 카메라가 열려 있는 동안 서비스워커 업데이트로 페이지가 새로고침되면
  // 스캔 중이던 화면이 갑자기 사라진다. 닫힐 때까지 새로고침을 미루게 한다.
  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }
    return markBusy();
  }, [isOpen]);

  const stopCamera = useCallback(() => {
    sessionRef.current += 1;

    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    if (streamRef.current) {
      stopMediaStream(streamRef.current);
      streamRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    detectedRef.current = false;
    setStatus("카메라를 준비하는 중입니다...");

    const sessionId = sessionRef.current + 1;
    sessionRef.current = sessionId;
    const isCancelled = () => sessionRef.current !== sessionId;

    let lastScanAt = 0;

    const scanFrame = (decodeQr) => {
      if (isCancelled() || detectedRef.current) {
        return;
      }

      const video = videoRef.current;
      const canvas = canvasRef.current;

      if (!video || !canvas || !video.videoWidth) {
        rafRef.current = requestAnimationFrame(() => scanFrame(decodeQr));
        return;
      }

      // 디코딩 한 번이 저사양 기기에서 수십 ms씩 걸린다. 매 프레임 돌리면
      // 메인 스레드를 다 써서 닫기 버튼이 늦게 반응하고 기기가 뜨거워진다.
      // 초당 12회면 QR을 비추는 순간 잡아내기에 충분하다.
      const now = performance.now();
      if (now - lastScanAt < SCAN_INTERVAL_MS) {
        rafRef.current = requestAnimationFrame(() => scanFrame(decodeQr));
        return;
      }
      lastScanAt = now;

      const scale = Math.min(1, SCAN_MAX_SIDE / Math.max(video.videoWidth, video.videoHeight));
      const width = Math.round(video.videoWidth * scale);
      const height = Math.round(video.videoHeight * scale);

      // 캔버스 크기를 다시 대입하면 비트맵이 초기화된다. 값이 바뀔 때만 건드린다.
      if (canvas.width !== width || canvas.height !== height) {
        canvas.width = width;
        canvas.height = height;
      }

      const ctx = canvas.getContext("2d", { willReadFrequently: true });
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const result = decodeQr(imageData.data, imageData.width, imageData.height, {
        inversionAttempts: "dontInvert",
      });

      if (result) {
        const deliveryId = extractDeliveryId(result.data);

        if (deliveryId) {
          detectedRef.current = true;
          stopCamera();
          onDetectRef.current(deliveryId);
          return;
        }

        // 배송 QR이 아닌 다른 코드를 읽은 경우. 계속 스캔한다.
        setStatus("배송 QR이 아닙니다. 영수증의 QR을 비춰주세요.");
      }

      rafRef.current = requestAnimationFrame(() => scanFrame(decodeQr));
    };

    const startCamera = async () => {
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        setStatus("이 기기에서는 카메라를 사용할 수 없습니다.");
        return;
      }

      try {
        // 카메라 권한 창이 뜨는 동안 디코더를 함께 받아둔다
        const decodeQrPromise = loadJsQr();

        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: "environment" },
          audio: false,
        });

        // 취소된 시도는 ref에 넣지 않는다. 넣어버리면 뒤이어 시작된 새 시도가
        // 보관해둔 스트림을 이 시도가 정리하며 밀어내, 실제로 켜져 있는 카메라를
        // 아무도 끄지 못하는 상태가 된다.
        if (isCancelled()) {
          stopMediaStream(stream);
          return;
        }
        streamRef.current = stream;

        const video = videoRef.current;
        if (video) {
          video.srcObject = stream;
          video.setAttribute("playsinline", "true");
          try {
            await video.play();
          } catch (playError) {
            console.warn("QR 스캐너 비디오 재생 실패:", playError);
          }
        }

        if (isCancelled()) {
          stopMediaStream(stream);
          if (streamRef.current === stream) {
            streamRef.current = null;
          }
          return;
        }

        // 디코더 로드 실패는 카메라 권한 문제가 아니다. 아래 catch로 흘려보내면
        // 화면에는 멀쩡히 미리보기가 뜬 채로 "권한을 허용해주세요"라고 안내하게 된다.
        let decodeQr;
        try {
          decodeQr = await decodeQrPromise;
        } catch (loadError) {
          console.error("QR 디코더 로드 실패:", loadError);
          stopMediaStream(stream);
          if (streamRef.current === stream) {
            streamRef.current = null;
          }
          setStatus("스캐너를 불러오지 못했습니다. 네트워크 확인 후 다시 시도해주세요.");
          return;
        }

        if (isCancelled()) {
          stopMediaStream(stream);
          if (streamRef.current === stream) {
            streamRef.current = null;
          }
          return;
        }

        setStatus("영수증의 QR을 사각형 안에 비춰주세요.");
        rafRef.current = requestAnimationFrame(() => scanFrame(decodeQr));
      } catch (err) {
        console.error("QR 스캐너 카메라 접근 실패:", err);
        setStatus("카메라를 열 수 없습니다. 브라우저 설정에서 카메라 권한을 허용해주세요.");
        toast({
          title: "카메라 접근 실패",
          description: err.message,
          status: "error",
          duration: 3000,
          isClosable: true,
        });
      }
    };

    startCamera();

    return () => {
      stopCamera();
    };
  }, [isOpen, stopCamera, toast]);

  const handleClose = () => {
    stopCamera();
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="full">
      <ModalOverlay bg="blackAlpha.900" />
      <ModalContent bg="black" m={0}>
        <ModalCloseButton color="white" zIndex={20} />
        <ModalBody p={0} display="flex" flexDirection="column" justifyContent="center" alignItems="center">
          <Box position="relative" width="100%" height="100vh">
            <video
              ref={videoRef}
              autoPlay
              playsInline
              muted
              webkit-playsinline="true"
              style={{ width: "100%", height: "100%", objectFit: "cover", backgroundColor: "#000" }}
            />

            {/* 조준 사각형. 기사님이 QR을 어디에 맞춰야 할지 알려준다 */}
            <Box
              position="absolute"
              top="50%"
              left="50%"
              transform="translate(-50%, -50%)"
              width="65vw"
              maxWidth="280px"
              height="65vw"
              maxHeight="280px"
              border="3px solid"
              borderColor="whiteAlpha.900"
              borderRadius="md"
              pointerEvents="none"
            />

            <Box position="absolute" bottom="12%" left="50%" transform="translateX(-50%)" width="90%" textAlign="center">
              <Text color="white" bg="blackAlpha.700" px={3} py={2} borderRadius="md" fontSize="sm">
                {status}
              </Text>
              <Button mt={4} onClick={handleClose} colorScheme="whiteAlpha" size="md">
                닫기
              </Button>
            </Box>
          </Box>

          <canvas ref={canvasRef} style={{ display: "none" }} />
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}
